import React from 'react';
import { Mail, Phone, Linkedin, Plus, X } from 'lucide-react';
import { Contact } from '../types/Contact';

interface ContactInfoProps {
    formData: Partial<Contact>;
    isEditing?: boolean;
    onInputChange?: (field: keyof Contact, value: any) => void;
}

export default function ContactInfo({ formData, isEditing = false, onInputChange }: ContactInfoProps) {
    // Collect all available emails
    const emails = [
        { label: 'Primary', value: formData.email, field: 'email' as keyof Contact },
        { label: formData.email_1_label || 'Email 1', value: formData.email_1_value, field: 'email_1_value' as keyof Contact },
        { label: formData.email_2_label || 'Email 2', value: formData.email_2_value, field: 'email_2_value' as keyof Contact },
        { label: formData.email_3_label || 'Email 3', value: formData.email_3_value, field: 'email_3_value' as keyof Contact },
        { label: 'Business', value: formData.business_email, field: 'business_email' as keyof Contact }
    ];

    // Collect all available phone numbers
    const phones = [
        { label: 'Primary', value: formData.phone, field: 'phone' as keyof Contact },
        { label: 'Mobile', value: formData.mobile, field: 'mobile' as keyof Contact },
        { label: formData.phone_1_label || 'Phone 1', value: formData.phone_1_value, field: 'phone_1_value' as keyof Contact },
        { label: formData.phone_2_label || 'Phone 2', value: formData.phone_2_value, field: 'phone_2_value' as keyof Contact },
        { label: formData.phone_3_label || 'Phone 3', value: formData.phone_3_value, field: 'phone_3_value' as keyof Contact },
        { label: 'Business', value: formData.business_phone, field: 'business_phone' as keyof Contact }
    ];

    // De-duplication function to merge identical values
    const deduplicateContacts = (contacts: Array<{ label: string; value: string | undefined; field: keyof Contact }>) => {
        const valueMap = new Map<string, { labels: string[]; field: keyof Contact; isGeneric: boolean }>();

        contacts.forEach(contact => {
            if (!contact.value || contact.value.trim() === '') return;

            const normalizedValue = contact.value.trim().toLowerCase();
            const isGenericLabel = contact.label.includes('1') || contact.label.includes('2') || contact.label.includes('3') || contact.label === 'Primary';

            if (valueMap.has(normalizedValue)) {
                const existing = valueMap.get(normalizedValue)!;
                // Only add the label if it's not generic or if we don't have any non-generic labels yet
                if (!isGenericLabel || existing.labels.every(label =>
                    label.includes('1') || label.includes('2') || label.includes('3') || label === 'Primary'
                )) {
                    if (!existing.labels.includes(contact.label)) {
                        existing.labels.push(contact.label);
                    }
                }
                // Use the first non-generic field we encounter
                if (!existing.isGeneric && isGenericLabel) {
                    // Keep the existing field
                } else if (existing.isGeneric && !isGenericLabel) {
                    existing.field = contact.field;
                    existing.isGeneric = false;
                }
            } else {
                valueMap.set(normalizedValue, {
                    labels: [contact.label],
                    field: contact.field,
                    isGeneric: isGenericLabel
                });
            }
        });

        return Array.from(valueMap.entries()).map(([value, data]) => ({
            label: data.labels.length > 1 ? data.labels.join(' / ') : data.labels[0],
            value: contacts.find(c => c.value?.trim().toLowerCase() === value)?.value || value,
            field: data.field,
            isDuplicate: data.labels.length > 1
        }));
    };

    // Get deduplicated lists
    const deduplicatedEmails = deduplicateContacts(emails);
    const deduplicatedPhones = deduplicateContacts(phones);

    const renderEditableField = (icon: React.ReactNode, value: string | undefined, field: keyof Contact, placeholder: string, isLink: boolean = false, label?: string, isDuplicate?: boolean) => {
        if (isEditing && onInputChange) {
            return (
                <div className="flex items-center text-gray-300">
                    {icon}
                    <input
                        type={isLink ? "url" : "text"}
                        value={value || ''}
                        onChange={(e) => onInputChange(field, e.target.value)}
                        placeholder={placeholder}
                        className="flex-1 ml-3 px-3 py-2 bg-gray-900 border border-gray-700 text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    />
                </div>
            );
        }

        if (!value) return null;

        return (
            <div className="flex items-center text-gray-300">
                {icon}
                <div className="flex-1 ml-3">
                    <div className="flex flex-col">
                        {isLink ? (
                            <a
                                href={value.startsWith('http') ? value : `https://${value}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-400 hover:text-blue-300 hover:underline break-all"
                            >
                                {value}
                            </a>
                        ) : (
                            <span className="text-blue-400">{value}</span>
                        )}
                        {label && isDuplicate && (
                            <span className="text-xs text-gray-500 mt-1">
                                {label}
                            </span>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="bg-gradient-to-br from-gray-800 to-gray-900 border border-gray-700 p-6">
            <h3 className="font-light text-white mb-6 flex items-center text-lg tracking-wide">
                <Mail className="w-5 h-5 mr-3 text-blue-400" />
                CONTACT INFORMATION
            </h3>
            <div className="space-y-3">
                {/* Deduplicated email fields */}
                {isEditing ? (
                    // In edit mode, show all fields for editing
                    emails.map((email, index) => {
                        const emailField = renderEditableField(
                            <Mail className="w-4 h-4 text-blue-400" />,
                            email.value,
                            email.field,
                            `${email.label} Email`,
                            false
                        );

                        if (email.value || isEditing) {
                            return <div key={`email-${index}`}>{emailField}</div>;
                        }
                        return null;
                    })
                ) : (
                    // In view mode, show deduplicated emails
                    deduplicatedEmails.map((email, index) => {
                        const emailField = renderEditableField(
                            <Mail className="w-4 h-4 text-blue-400" />,
                            email.value,
                            email.field,
                            `${email.label} Email`,
                            false,
                            email.label,
                            email.isDuplicate
                        );

                        if (email.value) {
                            return <div key={`dedup-email-${index}`}>{emailField}</div>;
                        }
                        return null;
                    })
                )}

                {/* Deduplicated phone fields */}
                {isEditing ? (
                    // In edit mode, show all fields for editing
                    phones.map((phone, index) => {
                        const phoneField = renderEditableField(
                            <Phone className="w-4 h-4 text-green-400" />,
                            phone.value,
                            phone.field,
                            `${phone.label} Phone`,
                            false
                        );

                        if (phone.value || isEditing) {
                            return <div key={`phone-${index}`}>{phoneField}</div>;
                        }
                        return null;
                    })
                ) : (
                    // In view mode, show deduplicated phones
                    deduplicatedPhones.map((phone, index) => {
                        const phoneField = renderEditableField(
                            <Phone className="w-4 h-4 text-green-400" />,
                            phone.value,
                            phone.field,
                            `${phone.label} Phone`,
                            false,
                            phone.label,
                            phone.isDuplicate
                        );

                        if (phone.value) {
                            return <div key={`dedup-phone-${index}`}>{phoneField}</div>;
                        }
                        return null;
                    })
                )}

                {/* LinkedIn URL - Always show when editing or has value */}
                {(isEditing || formData.linkedin_profile_url) && renderEditableField(
                    <Linkedin className="w-4 h-4 text-blue-500" />,
                    formData.linkedin_profile_url,
                    'linkedin_profile_url',
                    'LinkedIn Profile URL',
                    true
                )}

                {/* Show message if no contact info available and not editing */}
                {!isEditing && deduplicatedEmails.length === 0 && deduplicatedPhones.length === 0 && !formData.linkedin_profile_url && (
                    <div className="text-gray-500 text-sm text-center py-2">
                        No contact information available
                    </div>
                )}
            </div>
        </div>
    );
}
